/**
 * `SkillAdmin` — pagina admin per la gestione delle Skill (CRUD + N:N).
 *
 * Design ref: "Componenti chiave" → pagine admin; rotta `/admin/skills`
 * (admin: gestione skill, 5.2). La relazione Skill↔Competenza è molti‑a‑molti
 * (5.3): qui si associa una Skill a una o più Competenze (5.4) tramite
 * `competenceIds`.
 *
 * CRUD su `/api/skills`:
 *  - `GET /api/skills`   → elenco (con `competenceIds`);
 *  - `POST /api/skills`  → creazione (`{ name, competenceIds? }`);
 *  - `PUT /api/skills/:id` → aggiornamento (nome/associazioni);
 *  - `DELETE /api/skills/:id` → cancellazione.
 *
 * L'elenco è presentato con il componente generico `DataTable` (ordinabile,
 * paginato) e una ricerca client‑side per nome. Creazione e modifica (nome +
 * associazioni N:N) avvengono in una `Modal` condivisa; l'eliminazione è
 * un'azione di riga.
 *
 * L'elenco delle Competenze (`GET /api/competences`) offre le opzioni di
 * associazione N:N. La coerenza bidirezionale è mantenuta dall'API; dopo ogni
 * mutazione si invalidano entrambe le query. L'autorizzazione autorevole è
 * dell'API (403 per i non‑admin, 5.8).
 *
 * _Requirements: 5.1, 5.2._
 */
import { useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CompetenceDto,
  CreateSkillRequest,
  DeleteUnlinkedSkillsResponse,
  DepartmentDto,
  ImportSkillRow,
  ImportSkillsRequest,
  ImportSkillsResponse,
  SkillDto,
  SkillType,
  UpdateSkillRequest,
} from '../shared/dto';
import { SKILL_TYPE_LABELS, SKILL_TYPE_VALUES } from '../shared/dto';
import { useApiClient } from '../api/useApiClient';
import { useAuth } from '../auth/AuthProvider';
import { parseSkillsCsv, CsvParseError } from '../utils/parseSkillsCsv';
import {
  Alert,
  Badge,
  Button,
  DataTable,
  Field,
  Icon,
  Loading,
  Modal,
  PageHeader,
  TextArea,
  TextInput,
  Toggle,
  ChipAutocomplete,
  type Column,
  type SortDirection,
} from '../components/ui';

const skillsKey = ['skills'] as const;
const competencesKey = ['competences'] as const;
const departmentsKey = ['departments'] as const;

/** Confronto di stringhe già orientato per la direzione (per `DataTable`). */
function compareByName(a: SkillDto, b: SkillDto, direction: SortDirection): number {
  const sign = direction === 'desc' ? -1 : 1;
  const va = a.name.toLowerCase();
  const vb = b.name.toLowerCase();
  if (va < vb) return -sign;
  if (va > vb) return sign;
  return 0;
}

/** Stato di modifica: nome + associazioni correnti della skill in editing. */
interface EditingState {
  id: string;
  name: string;
  /** Descrizione della skill (testo libero, facoltativo). */
  description: string;
  /** Tipo della skill (`''` = non specificato). */
  type: '' | SkillType;
  competenceIds: string[];
  /** Se la skill è autovalutabile dallo User. */
  selfAssessable: boolean;
  /** Se la skill è valutabile tramite quiz AI. */
  aiAssessable: boolean;
}

/** Esito dell'import di un singolo file durante l'import bulk. */
interface BulkFileResult {
  /** Nome del file caricato (con estensione). */
  fileName: string;
  /** Nome competenza dedotto dal file (basename senza `.csv`). */
  competenceName: string;
  /** Esito: `ok` (importato), `unmatched` (nessuna competenza), `error`. */
  status: 'ok' | 'unmatched' | 'error';
  /** Riepilogo server quando `status === 'ok'`. */
  result?: ImportSkillsResponse;
  /** Messaggio d'errore quando `status === 'error'`. */
  message?: string;
}

/** Rimuove l'estensione `.csv` (case‑insensitive) dal nome file. */
function stripCsvExtension(fileName: string): string {
  return fileName.replace(/\.csv$/i, '');
}

/**
 * Normalizza un nome per il match bulk competenza↔file. Il carattere `/` non
 * è ammesso nei nomi file, quindi alcune competenze (es. "…Technician /
 * Specialist (GIT/S)") non possono avere un file dal nome identico: qui `/`,
 * `-` e `_` sono trattati come equivalenti e gli spazi ridondanti collassati,
 * così un file `… - Specialist (GIT-S).csv` combacia con la competenza.
 */
function normalizeForMatch(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[/_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

export default function SkillAdmin() {
  const api = useApiClient();
  const queryClient = useQueryClient();
  const { role } = useAuth();
  // Il manager può solo AGGIUNGERE skill al catalogo (`POST /api/skills` e
  // `/import`): modifica ed eliminazione (`PUT`/`DELETE`) sono riservate
  // all'admin lato API. Nascondiamo quindi le azioni di riga per il manager,
  // coerentemente con l'autorizzazione autorevole (403 per i non‑admin).
  const canModify = role === 'admin';

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [newType, setNewType] = useState<'' | SkillType>('');
  const [newCompetenceIds, setNewCompetenceIds] = useState<string[]>([]);
  // Nuove skill autovalutabili e valutabili con AI per default.
  const [newSelfAssessable, setNewSelfAssessable] = useState(true);
  const [newAiAssessable, setNewAiAssessable] = useState(true);
  const [editing, setEditing] = useState<EditingState | null>(null);
  const [query, setQuery] = useState('');
  // Filtro per competenza della tabella: `'all'` = tutte, `<id>` = solo le skill
  // collegate a quella competenza.
  const [competenceFilter, setCompetenceFilter] = useState<'all' | string>('all');
  // Filtro per tipologia: `'all'` = tutte, un `SkillType` specifico, oppure
  // `'none'` per le skill prive di tipologia (documenti legacy).
  const [typeFilter, setTypeFilter] = useState<'all' | SkillType | 'none'>('all');

  const skillsQuery = useQuery({
    queryKey: skillsKey,
    queryFn: () => api.get<SkillDto[]>('/api/skills'),
  });
  const competencesQuery = useQuery({
    queryKey: competencesKey,
    queryFn: () => api.get<CompetenceDto[]>('/api/competences'),
  });
  // Reparti: mappa id → nome per raggruppare le competenze del filtro per
  // reparto (optgroup).
  const departmentsQuery = useQuery({
    queryKey: departmentsKey,
    queryFn: () => api.get<DepartmentDto[]>('/api/departments'),
  });

  // Le associazioni sono bidirezionali: invalidiamo entrambi i cataloghi.
  const invalidateCatalog = () => {
    void queryClient.invalidateQueries({ queryKey: skillsKey });
    void queryClient.invalidateQueries({ queryKey: competencesKey });
  };

  const createSkill = useMutation({
    mutationFn: (body: CreateSkillRequest) => api.post<SkillDto>('/api/skills', body),
    onSuccess: () => {
      setNewName('');
      setNewDescription('');
      setNewType('');
      setNewCompetenceIds([]);
      setNewSelfAssessable(true);
      setNewAiAssessable(true);
      setIsCreateOpen(false);
      invalidateCatalog();
    },
  });

  const updateSkill = useMutation({
    mutationFn: (vars: { id: string; body: UpdateSkillRequest }) =>
      api.put<SkillDto>(`/api/skills/${vars.id}`, vars.body),
    onSuccess: () => {
      setEditing(null);
      invalidateCatalog();
    },
  });

  const deleteSkill = useMutation({
    mutationFn: (id: string) => api.delete<void>(`/api/skills/${id}`),
    onSuccess: () => invalidateCatalog(),
  });

  // --- Eliminazione delle skill non associate ad alcuna competenza ---------
  // Apertura del dialog di conferma e mutazione che invoca l'endpoint di bulk
  // delete. L'esito (numero e nomi delle skill rimosse) resta visibile nel
  // dialog finché l'utente non lo chiude.
  const [isDeleteUnlinkedOpen, setIsDeleteUnlinkedOpen] = useState(false);
  const deleteUnlinked = useMutation({
    mutationFn: () =>
      api.delete<DeleteUnlinkedSkillsResponse>('/api/skills/unlinked'),
    onSuccess: () => invalidateCatalog(),
  });

  const openDeleteUnlinked = () => {
    deleteUnlinked.reset();
    setIsDeleteUnlinkedOpen(true);
  };
  const closeDeleteUnlinked = () => {
    setIsDeleteUnlinkedOpen(false);
    deleteUnlinked.reset();
  };

  // --- Import da CSV -------------------------------------------------------
  const [isImportOpen, setIsImportOpen] = useState(false);
  const [importCompetenceId, setImportCompetenceId] = useState('');
  const [csvError, setCsvError] = useState<string | null>(null);
  const [importResult, setImportResult] = useState<ImportSkillsResponse | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // --- Import bulk (più CSV, competenza dedotta dal nome file) -------------
  const bulkInputRef = useRef<HTMLInputElement>(null);
  const [bulkRunning, setBulkRunning] = useState(false);
  const [bulkResults, setBulkResults] = useState<BulkFileResult[] | null>(null);

  const importSkills = useMutation({
    mutationFn: (rows: ImportSkillRow[]) => {
      const body: ImportSkillsRequest = {
        skills: rows,
        competenceId: importCompetenceId === '' ? null : importCompetenceId,
      };
      return api.post<ImportSkillsResponse>('/api/skills/import', body);
    },
    onSuccess: (result) => {
      setImportResult(result);
      invalidateCatalog();
    },
  });

  const openImport = () => {
    setImportResult(null);
    setCsvError(null);
    setImportCompetenceId('');
    setBulkResults(null);
    setBulkRunning(false);
    importSkills.reset();
    setIsImportOpen(true);
  };

  const closeImport = () => {
    setIsImportOpen(false);
    setCsvError(null);
    setImportResult(null);
    setBulkResults(null);
    setBulkRunning(false);
    importSkills.reset();
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (bulkInputRef.current) bulkInputRef.current.value = '';
  };

  const handleCsvFile = async (file: File) => {
    setCsvError(null);
    setImportResult(null);
    importSkills.reset();
    try {
      const text = await file.text();
      const rows = parseSkillsCsv(text);
      importSkills.mutate(rows);
    } catch (err) {
      setCsvError(
        err instanceof CsvParseError
          ? err.message
          : 'Impossibile leggere il file CSV.',
      );
    } finally {
      // Consente di riselezionare lo stesso file dopo un errore/una modifica.
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  /**
   * Import bulk: per ogni CSV selezionato deduce la competenza dal nome del
   * file (basename senza `.csv`, confronto case‑insensitive) e lancia l'import
   * con la relativa `competenceId`. I file senza una competenza corrispondente
   * (o non parsabili) sono segnalati, gli altri proseguono comunque. Gli import
   * sono eseguiti in sequenza per non sovraccaricare il server e per un
   * riepilogo deterministico.
   */
  const handleBulkFiles = async (files: File[]) => {
    setCsvError(null);
    setImportResult(null);
    importSkills.reset();
    setBulkResults(null);
    setBulkRunning(true);

    // Mappa nome competenza (normalizzato) → id, dall'ultima lista nota.
    const byName = new Map(
      (competencesQuery.data ?? []).map((c) => [normalizeForMatch(c.name), c]),
    );

    const results: BulkFileResult[] = [];
    for (const file of files) {
      const competenceName = stripCsvExtension(file.name);
      const match = byName.get(normalizeForMatch(competenceName));

      if (match === undefined) {
        results.push({
          fileName: file.name,
          competenceName,
          status: 'unmatched',
        });
        continue;
      }

      try {
        const text = await file.text();
        const rows = parseSkillsCsv(text);
        const body: ImportSkillsRequest = {
          skills: rows,
          competenceId: match.id,
        };
        const result = await api.post<ImportSkillsResponse>(
          '/api/skills/import',
          body,
        );
        results.push({
          fileName: file.name,
          competenceName: match.name,
          status: 'ok',
          result,
        });
      } catch (err) {
        results.push({
          fileName: file.name,
          competenceName: match.name,
          status: 'error',
          message:
            err instanceof CsvParseError
              ? err.message
              : 'Import non riuscito.',
        });
      }
    }

    setBulkResults(results);
    setBulkRunning(false);
    invalidateCatalog();
    if (bulkInputRef.current) bulkInputRef.current.value = '';
  };

  const skills = useMemo(() => skillsQuery.data ?? [], [skillsQuery.data]);
  // Competenze sempre in ordine alfabetico (case‑insensitive, locale it):
  // alimenta sia il select dell'import sia le opzioni di associazione N:N.
  const competences = useMemo(
    () =>
      [...(competencesQuery.data ?? [])].sort((a, b) =>
        a.name.localeCompare(b.name, 'it', { sensitivity: 'base' }),
      ),
    [competencesQuery.data],
  );

  const competenceName = useMemo(() => {
    const byId = new Map(competences.map((c) => [c.id, c.name]));
    return (id: string) => byId.get(id) ?? id;
  }, [competences]);

  // Skill non associate ad alcuna competenza ("orfane"): alimenta il conteggio
  // mostrato sul pulsante e nel dialog di conferma dell'eliminazione massiva.
  const unlinkedCount = useMemo(
    () => skills.filter((s) => s.competenceIds.length === 0).length,
    [skills],
  );

  // Competenze del filtro raggruppate per reparto per l'optgroup: reparti in
  // ordine alfabetico e competenze in ordine alfabetico dentro ciascuno;
  // "Senza reparto" (o reparto non più esistente) in coda. `competences` è già
  // ordinato per nome, quindi l'ordine dentro ogni gruppo è preservato.
  const competenceGroups = useMemo(() => {
    const departmentNameById = new Map(
      (departmentsQuery.data ?? []).map((d) => [d.id, d.name]),
    );
    const ORPHAN = '__none__';
    const byDepartment = new Map<string, CompetenceDto[]>();
    for (const c of competences) {
      const key =
        c.departmentId !== null && departmentNameById.has(c.departmentId)
          ? c.departmentId
          : ORPHAN;
      const list = byDepartment.get(key) ?? [];
      list.push(c);
      byDepartment.set(key, list);
    }
    const collator = new Intl.Collator('it', { sensitivity: 'base' });
    const groups = [...byDepartment.entries()].map(([key, items]) => ({
      key,
      label: key === ORPHAN ? 'Senza reparto' : departmentNameById.get(key) ?? 'Senza reparto',
      isOrphan: key === ORPHAN,
      competences: items,
    }));
    groups.sort((a, b) => {
      if (a.isOrphan !== b.isOrphan) return a.isOrphan ? 1 : -1;
      return collator.compare(a.label, b.label);
    });
    return groups;
  }, [competences, departmentsQuery.data]);

  // Ricerca client‑side per nome (case‑insensitive) + filtro per competenza
  // collegata (relazione N:N su `competenceIds`).
  const normalizedQuery = query.trim().toLowerCase();
  const visible = useMemo(() => {
    let result = skills;
    if (competenceFilter !== 'all') {
      result = result.filter((s) => s.competenceIds.includes(competenceFilter));
    }
    if (typeFilter !== 'all') {
      result = result.filter((s) =>
        typeFilter === 'none' ? s.type === undefined : s.type === typeFilter,
      );
    }
    if (normalizedQuery !== '') {
      result = result.filter((s) => s.name.toLowerCase().includes(normalizedQuery));
    }
    return result;
  }, [skills, competenceFilter, typeFilter, normalizedQuery]);

  const trimmedNew = newName.trim();
  const trimmedEdit = editing?.name.trim() ?? '';

  const columns = useMemo<Column<SkillDto>[]>(
    () => [
      {
        key: 'name',
        header: 'Skill',
        sortable: true,
        sortComparator: compareByName,
        render: (s) => s.name,
      },
      {
        key: 'type',
        header: 'Tipologia',
        // Tipologia e-CF come chip, "—" quando non specificata.
        render: (s) =>
          s.type === undefined ? (
            <span className="admin-table__assoc">—</span>
          ) : (
            <Badge tone="neutral">{SKILL_TYPE_LABELS[s.type]}</Badge>
          ),
      },
      {
        key: 'competences',
        header: 'Competenze associate',
        render: (s) => (
          <span className="admin-table__assoc">
            {s.competenceIds.length === 0
              ? '—'
              : s.competenceIds.map(competenceName).join(', ')}
          </span>
        ),
      },
      {
        key: 'assessment',
        header: 'Valutazione',
        // Le modalità di valutazione abilitate sono rese come chip (Badge),
        // con "—" quando nessuna è attiva.
        render: (s) => {
          if (!s.selfAssessable && !s.aiAssessable) {
            return <span className="admin-table__assoc">—</span>;
          }
          return (
            <div className="ui-chip-list ui-chip-list--nowrap">
              {s.selfAssessable && <Badge tone="primary">Auto</Badge>}
              {s.aiAssessable && <Badge tone="info">AI</Badge>}
            </div>
          );
        },
      },
      ...(canModify
        ? [
            {
              key: 'actions',
              header: 'Azioni',
              className: 'admin-table__actions-col',
              render: (s: SkillDto) => (
                <div className="admin-item__actions">
                  <Button
                    type="button"
                    size="sm"
                    iconOnly
                    aria-label={`Modifica ${s.name}`}
                    title={`Modifica ${s.name}`}
                    onClick={() =>
                      setEditing({
                        id: s.id,
                        name: s.name,
                        description: s.description ?? '',
                        type: s.type ?? '',
                        competenceIds: [...s.competenceIds],
                        selfAssessable: s.selfAssessable,
                        aiAssessable: s.aiAssessable,
                      })
                    }
                  >
                    <Icon name="edit" />
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="danger"
                    iconOnly
                    aria-label={`Elimina ${s.name}`}
                    title={`Elimina ${s.name}`}
                    onClick={() => deleteSkill.mutate(s.id)}
                    loading={deleteSkill.isPending}
                  >
                    <Icon name="trash" />
                  </Button>
                </div>
              ),
            } satisfies Column<SkillDto>,
          ]
        : []),
    ],
    [competenceName, deleteSkill, canModify],
  );

  return (
    <section className="skill-admin" aria-labelledby="skill-admin-title">
      <PageHeader
        title="Amministrazione — Skill"
        titleId="skill-admin-title"
        subtitle="Gestisci le skill e le competenze associate (relazione molti-a-molti)."
        actions={
          <div className="admin-toolbar">
            <Button type="button" variant="default" onClick={openImport}>
              Importa da CSV
            </Button>
            {canModify && (
              <Button
                type="button"
                variant="danger"
                onClick={openDeleteUnlinked}
                disabled={unlinkedCount === 0}
                title={
                  unlinkedCount === 0
                    ? 'Nessuna skill senza competenza associata'
                    : `Elimina ${unlinkedCount} skill senza competenza associata`
                }
              >
                Pulisci
                {unlinkedCount > 0 ? ` (${unlinkedCount})` : ''}
              </Button>
            )}
            <Button
              type="button"
              variant="primary"
              onClick={() => {
                setNewName('');
                setNewDescription('');
                setNewType('');
                setNewCompetenceIds([]);
                setNewSelfAssessable(true);
                setNewAiAssessable(true);
                setIsCreateOpen(true);
              }}
            >
              Nuova skill
            </Button>
          </div>
        }
      />

      {/* Ricerca a piena larghezza, stesso formato della pagina competenze. */}
      <Field
        id="skill-search"
        label="Cerca skill"
        hint="Filtra le skill per competenza, tipologia e nome."
      >
        <div className="competence-search">
          <span className="competence-search__department user-admin__select">
            <select
              id="skill-search-competenza"
              aria-label="Filtra skill per competenza"
              value={competenceFilter}
              disabled={competencesQuery.isLoading}
              onChange={(e) => setCompetenceFilter(e.target.value)}
            >
              <option value="all">Tutte le competenze</option>
              {competenceGroups.map((group) => (
                <optgroup key={group.key} label={group.label}>
                  {group.competences.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
          </span>
          <span className="competence-search__department user-admin__select">
            <select
              id="skill-search-tipologia"
              aria-label="Filtra skill per tipologia"
              value={typeFilter}
              onChange={(e) =>
                setTypeFilter(e.target.value as 'all' | SkillType | 'none')
              }
            >
              <option value="all">Tutte le tipologie</option>
              {SKILL_TYPE_VALUES.map((t) => (
                <option key={t} value={t}>
                  {SKILL_TYPE_LABELS[t]}
                </option>
              ))}
              <option value="none">Senza tipo</option>
            </select>
          </span>
          <TextInput
            id="skill-search"
            className="competence-search__input"
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Cerca per nome…"
            autoComplete="off"
            aria-label="Cerca skill per nome"
          />
        </div>
      </Field>

      {/* Conferma eliminazione massiva delle skill senza competenza associata.
          Operazione distruttiva: richiede conferma esplicita e mostra l'esito. */}
      <Modal
        open={isDeleteUnlinkedOpen}
        onClose={closeDeleteUnlinked}
        title="Elimina skill senza competenza"
        footer={
          <>
            <Button type="button" variant="ghost" onClick={closeDeleteUnlinked}>
              {deleteUnlinked.isSuccess ? 'Chiudi' : 'Annulla'}
            </Button>
            {!deleteUnlinked.isSuccess && (
              <Button
                type="button"
                variant="danger"
                loading={deleteUnlinked.isPending}
                disabled={unlinkedCount === 0}
                onClick={() => deleteUnlinked.mutate()}
              >
                Elimina definitivamente
              </Button>
            )}
          </>
        }
      >
        <div className="admin-form">
          {deleteUnlinked.isSuccess ? (
            <Alert tone={deleteUnlinked.data.deleted > 0 ? 'success' : 'info'}>
              {deleteUnlinked.data.deleted === 0
                ? 'Nessuna skill da eliminare.'
                : `${deleteUnlinked.data.deleted} skill eliminate.`}
              {deleteUnlinked.data.deletedNames.length > 0 && (
                <> ({deleteUnlinked.data.deletedNames.join(', ')})</>
              )}
            </Alert>
          ) : (
            <>
              <p>
                Verranno eliminate <strong>tutte le skill non associate ad
                alcuna competenza</strong>. L'operazione è irreversibile.
              </p>
              <p>
                Skill che verranno eliminate: <strong>{unlinkedCount}</strong>.
              </p>
              {deleteUnlinked.isError && (
                <Alert tone="error">
                  Eliminazione non riuscita. Riprova.
                </Alert>
              )}
            </>
          )}
        </div>
      </Modal>

      {/* Import skill da file CSV: seleziona la competenza a cui associarle
          tutte, poi scegli il file. De‑dup per nome lato server. */}
      <Modal
        open={isImportOpen}
        onClose={closeImport}
        title="Importa skill da CSV"
        footer={
          <>
            <Button type="button" variant="ghost" onClick={closeImport}>
              {importResult || bulkResults ? 'Chiudi' : 'Annulla'}
            </Button>
            <Button
              type="button"
              variant="default"
              loading={bulkRunning}
              disabled={importSkills.isPending || bulkRunning}
              onClick={() => bulkInputRef.current?.click()}
            >
              Importa più CSV (per nome file)…
            </Button>
            <Button
              type="button"
              variant="primary"
              loading={importSkills.isPending}
              disabled={importSkills.isPending || bulkRunning}
              onClick={() => fileInputRef.current?.click()}
            >
              Scegli file CSV…
            </Button>
          </>
        }
      >
        <div className="admin-form">
          <p>
            Carica un file CSV con una colonna <code>name</code> (o un elenco di nomi,
            uno per riga) e, opzionalmente, le colonne <code>description</code>,{' '}
            <code>level</code> e <code>weight</code>. Il nome è la chiave. Scegliendo
            una competenza, l'import ne sincronizza le skill: crea o aggiorna quelle
            nel file (applicando livello e peso) e scollega quelle non più presenti.
            La <code>description</code> è invece un attributo della skill e viene
            applicata sempre: senza selezionare alcuna competenza puoi importare il
            file per <strong>aggiornare solo le descrizioni</strong> delle skill
            esistenti.
          </p>
          <Field
            id="skill-import-competenza"
            label="Competenza da associare a tutte le skill importate"
          >
            <span className="user-admin__select user-admin__select--block">
              <select
                id="skill-import-competenza"
                value={importCompetenceId}
                disabled={importSkills.isPending}
                onChange={(e) => setImportCompetenceId(e.target.value)}
              >
                <option value="">Nessuna (solo creazione)</option>
                {competences.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </span>
          </Field>

          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,text/csv"
            className="sr-only"
            aria-label="File CSV da importare"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) void handleCsvFile(file);
            }}
          />

          <p className="admin-form__bulk-hint">
            In alternativa, con <strong>Importa più CSV</strong> puoi selezionare
            più file insieme: per ciascuno la competenza viene dedotta dal{' '}
            <em>nome del file</em> (senza <code>.csv</code>) e associata alle sue
            skill. I file il cui nome non corrisponde a una competenza esistente
            vengono elencati e saltati.
          </p>

          <input
            ref={bulkInputRef}
            type="file"
            accept=".csv,text/csv"
            multiple
            className="sr-only"
            aria-label="Più file CSV da importare (competenza dedotta dal nome file)"
            onChange={(e) => {
              const files = Array.from(e.target.files ?? []);
              if (files.length > 0) void handleBulkFiles(files);
            }}
          />

          {csvError && <Alert tone="error">{csvError}</Alert>}

          {bulkResults && (
            <div className="admin-form__bulk-results">
              {(() => {
                const okCount = bulkResults.filter((r) => r.status === 'ok').length;
                const unmatched = bulkResults.filter((r) => r.status === 'unmatched');
                const errored = bulkResults.filter((r) => r.status === 'error');
                return (
                  <Alert
                    tone={
                      errored.length > 0
                        ? 'error'
                        : unmatched.length > 0
                          ? 'info'
                          : 'success'
                    }
                  >
                    Import bulk completato: {okCount} file importati
                    {unmatched.length > 0 && (
                      <>, {unmatched.length} senza competenza corrispondente</>
                    )}
                    {errored.length > 0 && <>, {errored.length} con errori</>}.
                  </Alert>
                );
              })()}
              <ul className="admin-form__bulk-list">
                {bulkResults.map((r) => (
                  <li key={r.fileName}>
                    <strong>{r.fileName}</strong>
                    {r.status === 'ok' && r.result && (
                      <>
                        {' '}→ {r.competenceName}: {r.result.created} create,{' '}
                        {r.result.linked ?? 0} collegate, {r.result.updated ?? 0}{' '}
                        modificate, {r.result.removed ?? 0} cancellate
                      </>
                    )}
                    {r.status === 'unmatched' && (
                      <> → nessuna competenza chiamata «{r.competenceName}» (saltato)</>
                    )}
                    {r.status === 'error' && <> → errore: {r.message}</>}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {importSkills.isError && (
            <Alert tone="error">
              Import non riuscito. Verifica il file e riprova.
            </Alert>
          )}

          {importResult && (
            <Alert
              tone={
                importResult.created > 0 ||
                (importResult.linked ?? 0) > 0 ||
                (importResult.updated ?? 0) > 0 ||
                (importResult.removed ?? 0) > 0
                  ? 'success'
                  : 'info'
              }
            >
              {importResult.created} skill create,{' '}
              {importResult.linked ?? 0} collegate,{' '}
              {importResult.updated ?? 0} modificate,{' '}
              {importResult.removed ?? 0} cancellate
              {(importResult.linkedNames?.length ?? 0) > 0 && (
                <> (collegate: {importResult.linkedNames.join(', ')})</>
              )}
              {(importResult.updatedNames?.length ?? 0) > 0 && (
                <> (modificate: {importResult.updatedNames.join(', ')})</>
              )}
              {(importResult.removedNames?.length ?? 0) > 0 && (
                <> (cancellate: {importResult.removedNames.join(', ')})</>
              )}
              .
            </Alert>
          )}
        </div>
      </Modal>

      {/* Creazione con associazione N:N alle competenze (in modale condivisa). */}
      <Modal
        open={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
        title="Nuova skill"
        footer={
          <>
            <Button type="button" variant="ghost" onClick={() => setIsCreateOpen(false)}>
              Annulla
            </Button>
            <Button
              type="submit"
              form="skill-create-form"
              variant="primary"
              loading={createSkill.isPending}
              disabled={trimmedNew.length === 0}
            >
              Crea
            </Button>
          </>
        }
      >
        <form
          id="skill-create-form"
          className="admin-form"
          onSubmit={(e) => {
            e.preventDefault();
            if (trimmedNew.length === 0) return;
            const trimmedDescription = newDescription.trim();
            const body: CreateSkillRequest = {
              name: trimmedNew,
              ...(trimmedDescription !== '' ? { description: trimmedDescription } : {}),
              ...(newType !== '' ? { type: newType } : {}),
              competenceIds: newCompetenceIds,
              selfAssessable: newSelfAssessable,
              aiAssessable: newAiAssessable,
            };
            createSkill.mutate(body);
          }}
        >
          <Field id="skill-new-name" label="Nome">
            <TextInput
              id="skill-new-name"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="Es. React"
            />
          </Field>
          <Field
            id="skill-new-description"
            label="Descrizione"
            hint="Cosa significa possedere o valutare questa skill (facoltativo)."
          >
            <TextArea
              id="skill-new-description"
              rows={3}
              value={newDescription}
              onChange={(e) => setNewDescription(e.target.value)}
              placeholder="Es. Saper costruire interfacce a componenti con React…"
            />
          </Field>
          <Field
            id="skill-new-type"
            label="Tipo"
            hint="Classificazione e-CF della skill (facoltativo)."
          >
            <span className="user-admin__select user-admin__select--block">
              <select
                id="skill-new-type"
                value={newType}
                onChange={(e) => setNewType(e.target.value as '' | SkillType)}
              >
                <option value="">Non specificato</option>
                {SKILL_TYPE_VALUES.map((t) => (
                  <option key={t} value={t}>
                    {SKILL_TYPE_LABELS[t]}
                  </option>
                ))}
              </select>
            </span>
          </Field>
          <Field id="skill-new-competenze" label="Competenze associate">
            {competencesQuery.isLoading ? (
              <Loading>Caricamento competenze…</Loading>
            ) : (
              <ChipAutocomplete
                id="skill-new-competenze"
                options={competences}
                selectedIds={newCompetenceIds}
                onChange={setNewCompetenceIds}
                placeholder="Cerca una competenza…"
                emptyMessage="Nessuna competenza corrisponde."
              />
            )}
          </Field>
          <Field id="skill-new-options" label="Opzioni di valutazione">
            <div className="admin-form__checkboxes">
              <Toggle
                checked={newSelfAssessable}
                onChange={setNewSelfAssessable}
                label="Autovalutabile"
              />
              <Toggle
                checked={newAiAssessable}
                onChange={setNewAiAssessable}
                label="Valutabile con AI"
              />
            </div>
          </Field>
        </form>
      </Modal>

      {/* Modifica di una skill esistente: nome + associazioni N:N. */}
      <Modal
        open={editing !== null}
        onClose={() => setEditing(null)}
        title="Modifica skill"
        footer={
          <>
            <Button type="button" variant="ghost" onClick={() => setEditing(null)}>
              Annulla
            </Button>
            <Button
              type="submit"
              form="skill-edit-form"
              variant="primary"
              loading={updateSkill.isPending}
              disabled={trimmedEdit.length === 0}
            >
              Salva
            </Button>
          </>
        }
      >
        <form
          id="skill-edit-form"
          className="admin-form"
          onSubmit={(e) => {
            e.preventDefault();
            if (!editing || trimmedEdit.length === 0) return;
            const body: UpdateSkillRequest = {
              name: trimmedEdit,
              // Inviata sempre (anche vuota) per consentire la rimozione.
              description: editing.description.trim(),
              // Il tipo è inviato solo se selezionato: "Non specificato" non
              // modifica il valore esistente (coerente con l'import CSV).
              ...(editing.type !== '' ? { type: editing.type } : {}),
              competenceIds: editing.competenceIds,
              selfAssessable: editing.selfAssessable,
              aiAssessable: editing.aiAssessable,
            };
            updateSkill.mutate({ id: editing.id, body });
          }}
        >
          <Field id="skill-edit-name" label="Nome">
            <TextInput
              id="skill-edit-name"
              value={editing?.name ?? ''}
              onChange={(e) =>
                setEditing((prev) => (prev ? { ...prev, name: e.target.value } : prev))
              }
            />
          </Field>
          <Field
            id="skill-edit-description"
            label="Descrizione"
            hint="Cosa significa possedere o valutare questa skill (facoltativo)."
          >
            <TextArea
              id="skill-edit-description"
              rows={3}
              value={editing?.description ?? ''}
              onChange={(e) =>
                setEditing((prev) =>
                  prev ? { ...prev, description: e.target.value } : prev,
                )
              }
              placeholder="Es. Saper costruire interfacce a componenti con React…"
            />
          </Field>
          <Field
            id="skill-edit-type"
            label="Tipo"
            hint="Classificazione e-CF della skill (facoltativo)."
          >
            <span className="user-admin__select user-admin__select--block">
              <select
                id="skill-edit-type"
                value={editing?.type ?? ''}
                onChange={(e) =>
                  setEditing((prev) =>
                    prev
                      ? { ...prev, type: e.target.value as '' | SkillType }
                      : prev,
                  )
                }
              >
                <option value="">Non specificato</option>
                {SKILL_TYPE_VALUES.map((t) => (
                  <option key={t} value={t}>
                    {SKILL_TYPE_LABELS[t]}
                  </option>
                ))}
              </select>
            </span>
          </Field>
          <Field id="skill-edit-competenze" label="Competenze associate">
            <ChipAutocomplete
              id="skill-edit-competenze"
              options={competences}
              selectedIds={editing?.competenceIds ?? []}
              onChange={(next) =>
                setEditing((prev) => (prev ? { ...prev, competenceIds: next } : prev))
              }
              placeholder="Cerca una competenza…"
              emptyMessage="Nessuna competenza corrisponde."
            />
          </Field>
          <Field id="skill-edit-options" label="Opzioni di valutazione">
            <div className="admin-form__checkboxes">
              <Toggle
                checked={editing?.selfAssessable ?? true}
                onChange={(selfAssessable) =>
                  setEditing((prev) =>
                    prev ? { ...prev, selfAssessable } : prev,
                  )
                }
                label="Autovalutabile"
              />
              <Toggle
                checked={editing?.aiAssessable ?? true}
                onChange={(aiAssessable) =>
                  setEditing((prev) =>
                    prev ? { ...prev, aiAssessable } : prev,
                  )
                }
                label="Valutabile con AI"
              />
            </div>
          </Field>
        </form>
      </Modal>

      {(createSkill.isError || updateSkill.isError || deleteSkill.isError) && (
        <Alert tone="error">Impossibile salvare la modifica. Riprova.</Alert>
      )}

      {skillsQuery.isLoading ? (
        <Loading>Caricamento skill…</Loading>
      ) : (
        <DataTable
          rows={visible}
          columns={columns}
          rowKey={(s) => s.id}
          error={skillsQuery.isError ? 'Impossibile caricare le skill.' : null}
          emptyMessage={
            normalizedQuery !== '' || competenceFilter !== 'all'
              ? 'Nessuna skill corrisponde ai filtri selezionati.'
              : 'Nessuna skill configurata. Crea la prima con “Nuova skill”.'
          }
          defaultSort={{ key: 'name', direction: 'asc' }}
          pageSize={10}
        />
      )}
    </section>
  );
}
